import { graphql } from "gatsby"
import { AnimatePresence, motion } from "framer-motion"

import { Box, Container, Flex, Text, styled } from "@mode/components"
import { useCallback, useState } from "react"
import Image from 'gatsby-plugin-sanity-image'

const StyledExpandable = styled('div', {
  border: '1px solid black',
  borderColor: '$g4',
  mb: '10px',
  borderRadius: '$16',
  overflow: 'hidden',
  transition: 'background 200ms ease-in-out',
  variants: {
    active: {
      true: {
        background: '$g2',
        transition: 'background 200ms ease-in-out',
      },
      false: {
        background: '$g1',
        transition: 'background 200ms ease-in-out',
      }
    }
  }
})

const Content = styled(motion.div, {
  position: 'relative',
  top: '-4px',
})

const Expandable = ({ id, heading, text, active, onClick }) => {
  return (
    <StyledExpandable active={active}>
      <Text
        textStyle="p1"
        as="button"
        css={{ fontWeight: '500', py: '$xxxs', px: '$xxs', cursor: 'pointer', display: 'block', width: '100%' }}
        aria-expanded={false}
        aria-controls={`${heading} Content`}
        onClick={() => onClick(id)}
      >
        {heading}
      </Text>
      <Box>
        <AnimatePresence initial={false}>
          {active && <Content
            id={`${heading} Content`}
            key="content"
            initial={{
              height: 0,
              opacity: 0
            }}
            animate={{
              height: 'auto',
              opacity: 1
            }}
            exit={{
              height: 0,
              opacity: 0
            }}
            transition={{
              duration: 0.5,
              ease: [1, 0.1, 0.1, 1]
            }}
          >
            <Text textStyle="p2" css={{ px: '$xxs', pb: '$xxxs' }}>{text}</Text>
          </Content>}
        </AnimatePresence>
      </Box>
    </StyledExpandable >
  )
}

const StyledImage = styled(motion.div, {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  borderRadius: '$16',
  overflow: 'hidden',
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
})

export const ExpandableCarousel = ({ items, formattingOptions, background }) => {
  const [activeID, setActiveID] = useState(items[0]._key);
  const isActive = useCallback((ID) => {
    return activeID === ID;
  }, [activeID])

  const setActiveExpandable = (ID) => {
    setActiveID(ID);
  }
  const backgroundColor = formattingOptions?.background || background;
  return (
    <Box css={{ display: formattingOptions?.hidden ? 'none' : 'block', background: backgroundColor ?? '$g1', color: "$g5" }}>
      <Container css={{
        pt: formattingOptions?.paddingTop ?? "$sm",
        pb: formattingOptions?.paddingBottom ?? "$md",
      }}>
        <Flex justify="space-between" wrap="wrap">
          <Flex.Item css={{ width: '100%', '@md': { width: 'calc(40% - 10px)' } }}>
            {items.map((item, index) => {
              return <Expandable key={item._key} {...item} id={item._key} active={isActive(item._key)} onClick={setActiveExpandable} />
            })}
          </Flex.Item>
          <Flex.Item css={{ width: '100%', order: -1, position: 'relative', pt: '$xxs', pl: '$xxs', mb: '$xxs', overflow: 'hidden', background: '$g2', borderRadius: '$16', '@md': { mb: 0, width: 'calc(60% - 10px)', order: 2, pt: '$sm', pl: '$sm' } }}>
            <Box css={{ width: '100%', height: 0, pb: '90%', position: 'relative' }}>
              {items.map(item => {
                return (
                  <StyledImage
                    key={item._key}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isActive(item._key) ? 1 : 0 }}
                    css={{ zIndex: isActive(item._key) ? 2 : 1 }}
                    transition={{
                      duration: 0.3,
                      ease: 'linear',
                      delay: isActive(item._key) ? 0 : 0.5
                    }}
                  >
                    {item.image && <Image {...item.image} style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left' }} />}
                  </StyledImage>
                )
              })}
            </Box>
          </Flex.Item>
        </Flex>
      </Container>
    </Box>
  )
}

export const query = graphql`
  fragment ExpandableCarouselContent on ExpandableCarousel {
    _key
    background
    items {
      _key
      heading
      text
      image {
        ...ImageWithPreview
      }
    }
    formattingOptions {
      ...FormattingOptionsContent
    }
  }
`